import { apiUrls } from 'constants/urls';
import asyncErrorHandler, { IErrorHandlerResponse } from 'utils/requests/errorHandler';
import request from './request.service';
import filterEmptyParams from 'utils/common/filterEmptyParams';

import { AccountListItemType } from 'types/accounts';

import {
  UserAccountWalletType,
  UserBtcMiningType,
  UserGeneralInfoType,
  UserHistoryAction,
  UserHistoryType,
  UserListItemType,
  UserPaymentSettingsType,
  UserSearchType,
} from 'types/user';
import { IdType } from 'types/common';
import { PaginatedParamsType, PaginatedRefactorType, PaginatedType } from 'types/pagination';

interface SearchUsersParams extends Partial<PaginatedParamsType> {
  search: string;
}

type SearchUsersResponse = IErrorHandlerResponse<PaginatedType<UserSearchType[]>>;

export const searchUsers = async ({ search, page, per_page }: SearchUsersParams): Promise<SearchUsersResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.search, {
      method: 'GET',
      params: filterEmptyParams({ q: search, page, per_page }),
    });
  };

  return await asyncErrorHandler(callback, { defaultErrorReturn: [] });
};

interface GetUserGeneralInfoParams {
  uid: IdType;
}

type GetUserGeneralInfoResponse = IErrorHandlerResponse<UserGeneralInfoType | null>;

export const getUserGeneralInfo = async ({ uid }: GetUserGeneralInfoParams): Promise<GetUserGeneralInfoResponse> => {
  const callback = async () => {
    const response = await request(apiUrls.users.byUid(uid), {
      method: 'GET',
    });

    return response.data;
  };

  return await asyncErrorHandler(callback);
};

interface GetUsersWalletParams {
  uid: IdType;
}

type GetUsersWalletResponse = IErrorHandlerResponse<UserAccountWalletType | null>;

export const getUsersWallet = async ({ uid }: GetUsersWalletParams): Promise<GetUsersWalletResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.walletByUid(uid), {
      method: 'GET',
    });
  };

  return await asyncErrorHandler(callback);
};

interface GetUsersBtcMiningParams {
  uid: IdType;
}

type GetUsersBtcMiningResponse = IErrorHandlerResponse<UserBtcMiningType | null>;

export const getUsersBtcMining = async ({ uid }: GetUsersBtcMiningParams): Promise<GetUsersBtcMiningResponse> => {
  const callback = async () => {
    const response = await request(apiUrls.users.btcMiningByUid(uid), {
      method: 'GET',
    });

    return response.data;
  };

  return await asyncErrorHandler(callback);
};

interface GetUsersPaymentSettingsParams {
  uid: IdType;
}

type GetUsersPaymentSettingsResponse = IErrorHandlerResponse<UserPaymentSettingsType>;

export const getUsersPaymentSettings = async ({
  uid,
}: GetUsersPaymentSettingsParams): Promise<GetUsersPaymentSettingsResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.paymentSettingsById(uid));
  };

  return await asyncErrorHandler(callback);
};

export interface GetUserHistoryParams extends PaginatedParamsType {
  uid: IdType;
  action?: UserHistoryAction;
}

export type GetUserGeneralInfoHistoryResponse = IErrorHandlerResponse<PaginatedType<UserHistoryType[]> | null>;

export const getUserHistory = async ({
  uid,
  page,
  action,
  per_page,
}: GetUserHistoryParams): Promise<GetUserGeneralInfoHistoryResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.historyById(uid), {
      params: filterEmptyParams({ page, per_page, action }),
    });
  };

  return await asyncErrorHandler(callback);
};

export interface GetUsersParams extends PaginatedParamsType {
  search?: string;
}

export type GetUsersResponse = IErrorHandlerResponse<PaginatedRefactorType<UserListItemType[]> | null>;

export const getUsers = async ({ search, page, per_page }: GetUsersParams): Promise<GetUsersResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.all, {
      params: filterEmptyParams({ page, per_page, search }),
    });
  };

  return await asyncErrorHandler(callback);
};

export type UserAccountFee = AccountListItemType;

export interface UserAccountsFeesResponse {
  data?: AccountListItemType[];
}

export interface GetUserAccountsFeesParams {
  user_id: IdType;
}

export type GetUserAccountsFeesResponse = IErrorHandlerResponse<UserAccountsFeesResponse | null>;

export const getUserAccountsFees = async ({
  user_id,
}: GetUserAccountsFeesParams): Promise<GetUserAccountsFeesResponse> => {
  const callback = async () => {
    return await request(apiUrls.users.accountFees(user_id), {
      method: 'GET',
    });
  };

  return await asyncErrorHandler(callback);
};

export interface UpdateUserAccountsFeesParams {
  user_id: IdType;
  apply_to_all_accounts: boolean;
  account_pps_fee?: number;
  account_pplns_fee?: number;
  account_fees?: Array<{ account_id: IdType; pps_fee: number; pplns_fee: number }>;
}

export type UpdateUserAccountsFeesResponse = IErrorHandlerResponse<null>;

export const updateUserAccountsFees = async ({
  user_id,
  apply_to_all_accounts,
  account_pps_fee,
  account_pplns_fee,
  account_fees,
}: UpdateUserAccountsFeesParams): Promise<UpdateUserAccountsFeesResponse> => {
  const callback = async () => {
    const body = apply_to_all_accounts
      ? { apply_to_all_accounts: true, account_pps_fee, account_pplns_fee }
      : { apply_to_all_accounts: false, account_fees };

    return await request(apiUrls.users.accountFees(user_id), {
      method: 'POST',
      body,
    });
  };

  return await asyncErrorHandler(callback);
};
